import React, { useState, useRef } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { getFullUrl } from '../../utils/utils';
import RippleButton from './RippleButton';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import PropTypes from 'prop-types';

const Navbar = ({
  NavLogo,
  logoClasses = 'w-60 sm:w-64 md:w-80 lg:w-auto max-w-80',
  subdomain,
  menuItems,
}) => {
  const menuRef = useRef();
  const closeButtonRef = useRef();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useOnClickOutside(menuRef, () => setIsMenuOpen(false), closeButtonRef);

  const brandSlug = window.gon.current_brand ? window.gon.current_brand : 'shipsticks';

  return (
    <nav className="relative z-10 border-b-[1px] border-gray-50 border-opacity-30 py-3">
      <div className="mx-auto flex justify-between items-center px-4 sm:px-8 py-3
       lg:py-5 xl:px-40 2xl:px-60 page-navbar">
        <a href={getFullUrl('/', subdomain)} >
          <img src={NavLogo} alt="Nav Logo" className={`min-h-10 ${logoClasses}`} />
        </a>

        <button ref={closeButtonRef} onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white lg:hidden">
          {
            isMenuOpen ? <XMarkIcon  className="h-8 w-8 sm:h-10 sm:w-10" /> :
            <Bars3Icon className="h-8 w-8 sm:h-10 sm:w-10" />
          }
        </button>

        {/* Menu Items for mobile */}
        {menuItems.length > 0 && (
          <Transition
            ref={menuRef}
            show={isMenuOpen}
            enter="transition ease-out duration-300 transform"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-200 transform"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-full"
            className="fixed inset-x-0 top-[81px] sm:top-[89px] lg:hidden z-50 shadow-md bg-white"
          >
            <ul className="flex flex-col">
              {menuItems.slice(0, -1).map((item) => (
                <RippleButton
                  key={item.name}
                  onClick={() => window.location.href = getFullUrl(item.path, subdomain)}
                  className={
                    `flex items-center justify-start text-black h-16 pl-8 text-base border-b-[1px] border-gray-200
                    ${item.extraClasses || ''}`
                  }
                >
                  <span>{item.name}</span>
                </RippleButton>
              ))}
              <RippleButton
                onClick={() => window.location.href = getFullUrl(menuItems[menuItems.length - 1].path, subdomain)}
                className={`
                  flex items-center justify-center bg-${brandSlug} text-white rounded-full
                  py-2 px-4 my-6 h-[48px] w-3/4 max-w-72 mx-auto
                `}
              >
                <span>{menuItems[menuItems.length - 1].name}</span>
              </RippleButton>
            </ul>
          </Transition>
        )}

        {/* Menu Items for desktop */}
        <ul className="hidden lg:flex items-center space-x-4">
          {menuItems.map((item, index) => (
            <li
              key={item.name}
              onClick={() => window.location.href = getFullUrl(item.path, subdomain)}
              className={
                `text-white text-base cursor-pointer ${item.customClasses}
                ${item.extraClasses || ''}`}

            >
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  NavLogo: PropTypes.string.isRequired,
  logoClasses: PropTypes.string,
  subdomain: PropTypes.string,
  showExtraLinks: PropTypes.bool,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      order: PropTypes.number,
      path: PropTypes.string.isRequired,
      customClasses: PropTypes.string,
    }).isRequired
  ).isRequired,
};

Navbar.defaultProps = {
  defaultMenuItems: null
}

export default Navbar;
